import React, { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "../../../components/tables/hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../../utils/userSettings.js";
import { Box, Chip, Typography } from "@mui/material";
import UserAvatar from "../../../components/UserAvatar.jsx";
import { getDatumZeit } from "@accredo/fe-shared/services/formatService.js";

export default function BlogPostsTable({ onRowClick = () => {} }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();

  const tableKey = "blog-posts";
  const storageKey = "v2_table_blog-posts";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "News-Beiträge",
        route: "data/blog-posts",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          include: ["creator", "authors", "roles", "tags"],
          fields: [
            "id",
            "overline",
            "status",
            "headline",
            "teaser",
            "published_at",
            "created_at",
            "updated_at",
            "created_by",
          ],
          filters: { published: true },
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKeys: ["overline", "headline", "teaser"],
      header: "",
      Cell: ({ row }) => {
        const blogPost = row.original;
        if (!blogPost) {
          return null;
        }

        return (
          <>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Box>
                  <Box display="flex" gap={1}>
                    {blogPost.tags?.map((tag) => (
                      <Chip
                        key={`tag_${tag.id}`}
                        variant="filled"
                        size="small"
                        label={tag.name}
                      />
                    ))}
                  </Box>
                </Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {blogPost.overline}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  {getDatumZeit(blogPost?.published_at)}
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle1" color="textSecondary">
              {blogPost.headline}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {blogPost.teaser}
            </Typography>

            <Box display="flex" gap={1} mt={2}>
              {blogPost.authors?.map((author) => (
                <Box key={author.id} py={1}>
                  <UserAvatar
                    user={author}
                    enableTooltip={true}
                    showLabel={true}
                  />
                </Box>
              ))}
            </Box>
          </>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader={false}
      enableTopToolbar={false}
      enableSorting={false}
      enableGlobalFilter={false}
      enableColumnFilters={false}
      enableColumnHiding={false}
      topToolbarIcons={false}
      onRowClick={onRowClick}
      viewMode="list"
      variant="bare"
    />
  );
}

BlogPostsTable.propTypes = {
  onRowClick: PropTypes.func,
};
